import { useAuth } from "@auth/AuthContext"
import { ClassroomIcon } from "@components/ClassroomIcon"
import { SchoolMark } from "@components/SchoolMark"
import { Avatar } from "@components/ui/Avatar"
import { Button } from "@components/ui/Button"
import { Segmented } from "@components/ui/Segmented"
import { isDirector } from "@features/direction"
import { useFeedback } from "@features/feedback"
import { NotificationBell } from "@features/notifications"
import { cn } from "@lib/cn"
import { useTheme, useSystemThemeListener } from "@lib/theme"
import {
  BookOpen,
  CalendarClock,
  ChevronDown,
  Compass,
  FileText,
  LogOut,
  MessageSquare,
  Minus,
  Plus,
  ScanText,
  Settings,
  Sparkles,
  X,
  Library,
} from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useLocation, useNavigate } from "react-router-dom"

import { ALL_CLASSROOMS } from "@/constants/classroomScope"
import { REFERENCES_ROUTE } from "@/constants/references"
import { THEME_OPTIONS } from "@/constants/theme"
import type { Classroom, Professor } from "@/types"

// Row styling shared by every navigable entry in the sidebar — primary nav,
// the two classroom sections, and the pinned tray. Extracted so the active /
// idle treatment is defined once (`dry-principles.md`).
const navItemClass = (isActive: boolean) =>
  cn(
    // 44px on a phone, the compact 36 from `md` up: this is the product's most
    // tapped surface and it is tapped one-handed, standing up.
    "rounded-control flex min-h-11 items-center gap-3 px-3 text-sm transition-colors duration-150 md:min-h-9",
    isActive ? "bg-inset text-ink font-medium" : "text-ink-muted hover:bg-inset hover:text-ink",
  )

interface NavItem {
  to: string
  icon: ReactNode
  labelKey: string
  // Optional display gate. When present, the item only renders if the
  // predicate returns true for the current professor (PRD-17 §FR-6).
  gate?: (professor: Professor) => boolean
}

// "Materiales" sits at #2, right below the "Generar material" CTA, so the
// create action and the library it feeds read as one block (PRD-01 §FR-19).
const PRIMARY_NAV: ReadonlyArray<NavItem> = [
  // PRD-59 — "Hoy", not "Inicio": the one nav word that named a location
  // instead of a job, on the one surface whose whole value is being dated.
  { to: "/", icon: <CalendarClock className="h-4 w-4" />, labelKey: "nav.home" },
  { to: "/materiales", icon: <FileText className="h-4 w-4" />, labelKey: "nav.materials" },
  // PRD-64 — "Fuentes" right under Materiales: the library the generator
  // reads from. Promoted from a row inside /materiales (2026-10-08) because
  // nobody found it there.
  { to: REFERENCES_ROUTE, icon: <Library className="h-4 w-4" />, labelKey: "nav.references" },
  { to: "/salones", icon: <BookOpen className="h-4 w-4" />, labelKey: "nav.classrooms" },
  {
    to: "/direccion",
    icon: <Compass className="h-4 w-4" />,
    labelKey: "nav.direction",
    gate: isDirector,
  },
]

interface SidebarProps {
  professor: Professor
  classrooms: ReadonlyArray<Classroom>
  onLogout: () => void
  onCloseMobile?: () => void
  showCloseButton?: boolean
}

export function Sidebar({
  professor,
  classrooms,
  onLogout,
  onCloseMobile,
  showCloseButton = false,
}: SidebarProps) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const theme = useTheme()
  const { setTheme } = useAuth()
  useSystemThemeListener()
  const [classesOpen, setClassesOpen] = useState(true)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // react-router's NavLink matches on pathname only, so every
  // `/materiales?classroom=…` row would read as active at once. The materials
  // scope lives in a search param, so its active state is computed by hand.
  const location = useLocation()
  const materialsScope = new URLSearchParams(location.search).get("classroom")
  const onMaterialsList = location.pathname === "/materiales"
  // The primary "Materiales" entry owns the cross-classroom list plus every
  // detail page (`/materiales/:id`), which has no scope of its own.
  const materialsAllActive =
    (onMaterialsList && (!materialsScope || materialsScope === ALL_CLASSROOMS)) ||
    location.pathname.startsWith("/materiales/")
  const isMaterialsClassroomActive = (id: string) => onMaterialsList && materialsScope === id

  useEffect(() => {
    if (!userMenuOpen) return
    function onClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    function onKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") setUserMenuOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    document.addEventListener("keydown", onKeydown)
    return () => {
      document.removeEventListener("mousedown", onClickOutside)
      document.removeEventListener("keydown", onKeydown)
    }
  }, [userMenuOpen])

  const handleSettings = () => {
    setUserMenuOpen(false)
    onCloseMobile?.()
    navigate("/configuracion")
  }

  const handleLogout = () => {
    setUserMenuOpen(false)
    onCloseMobile?.()
    onLogout()
  }

  const handleGenerateMaterial = () => {
    onCloseMobile?.()
    navigate("/materiales?new=1")
  }

  return (
    <aside
      className="border-border bg-surface flex h-full w-72 flex-col border-r"
      aria-label={t("nav.aria.primary")}
    >
      <div className="border-hairline flex items-center justify-between border-b px-4 py-4">
        {/* PRD-55 — the school leads, Platonica moves to the subtitle
            slot that already existed. With no school and no crest this
            renders exactly what it rendered before. */}
        <SchoolMark
          logoUrl={professor?.school?.logo_url}
          schoolName={professor?.school?.name}
          subtitle={t("app.cobrand_subtitle")}
          fallbackName={t("app.name")}
          className="min-w-0"
        />
        <div className="flex items-center gap-2">
          {/* PRD-26 §FR-10 — bell lives in the sidebar header right
              cluster, next to the (mobile-only) close button. Hidden
              on the desktop sidebar header only on the rare case where
              the user menu is also showing; sized to match the close
              button so the row stays visually balanced. */}
          <NotificationBell />
          {showCloseButton && (
            <button
              type="button"
              onClick={onCloseMobile}
              aria-label={t("nav.aria.close_menu")}
              className="rounded-control text-ink-muted hover:bg-inset hover:text-ink flex h-11 w-11 items-center justify-center md:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* PRD-01 §FR-19 — the product's primary action, pinned above the nav.
          The only solid-color surface in the sidebar so it reads as an
          action, not a destination. Deep-links to /materiales?new=1, which
          auto-opens the request modal (PRD-10 §FR-4 mechanism). */}
      <div className="px-3 pt-4">
        <Button
          onClick={handleGenerateMaterial}
          leftIcon={<Sparkles className="h-4 w-4" />}
          className="w-full font-semibold shadow-sm"
        >
          {t("nav.generate_material")}
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {PRIMARY_NAV.filter((item) => !item.gate || item.gate(professor)).map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                onClick={onCloseMobile}
                className={
                  item.to === "/materiales"
                    ? navItemClass(materialsAllActive)
                    : ({ isActive }) => navItemClass(isActive)
                }
              >
                <span className="text-ink-subtle">{item.icon}</span>
                <span>{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* One list, two destinations per row (2026-08-10).
            "MIS SALONES" and "MIS MATERIALES" used to be two sections
            rendering the SAME classroom names back to back — on the
            mobile drawer a professor with 8 groups scrolled past 16
            rows of duplicated names to reach either destination. The
            row now carries both: the name navigates to the roster
            (the more frequent destination, so it keeps the whole-row
            target), and a trailing icon button opens that classroom's
            materials. Same dual-CTA shape `ClassroomCard` already uses
            on /salones, so the two surfaces teach one pattern.
            The cross-classroom materials view is untouched — it is
            still the primary "Materiales" entry above (PRD-05 keeps it
            as the default view, KPIs across every group). */}
        <CollapsibleNavSection
          id="sidebar-my-classes"
          title={t("nav.my_classes")}
          listLabel={t("nav.aria.my_classes")}
          open={classesOpen}
          onToggle={() => setClassesOpen((open) => !open)}
        >
          {classrooms.map((classroom) => (
            <li key={classroom.id} className="group/row relative">
              <NavLink
                to={`/salones/${classroom.id}/alumnos`}
                onClick={onCloseMobile}
                className={({ isActive }) => cn(navItemClass(isActive), "pr-12 md:pr-10")}
              >
                <ClassroomIcon classroom={classroom} className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{classroom.effective_display_name}</span>
                {/* The group average used to ride here as a colour-coded pill.
                    It is gone (PRD-59): a number in a nav has no baseline — no
                    group comparison, no delivery rate, no "needs attention"
                    reason — so it could only be read as a verdict, and PRD-40
                    §FR-11 is explicit that this figure is informational and
                    never a signal. Worse, `avg_grade ? …` hid it at zero, so a
                    group with no grades yet and a group averaging zero rendered
                    identically. Both destinations one tap away show the figure
                    with the context that makes it mean something. */}
              </NavLink>
              {/* Sibling of the NavLink, not a child: nested anchors are
                  invalid HTML (the same constraint `ClassroomCard` works
                  around with navigate + stopPropagation). Absolutely
                  positioned over the row's reserved `pr-10` gutter so
                  the name still truncates against it rather than under
                  it. Always rendered — never hover-only — because on
                  touch there is no hover and this is a primary route. */}
              <NavLink
                to={`/materiales?classroom=${classroom.id}`}
                onClick={onCloseMobile}
                aria-label={t("nav.row.view_materials", {
                  classroom: classroom.effective_display_name,
                })}
                title={t("nav.row.view_materials", {
                  classroom: classroom.effective_display_name,
                })}
                className={cn(
                  "rounded-control absolute top-1/2 right-0.5 flex h-11 w-11 -translate-y-1/2 items-center justify-center transition-colors duration-150 md:h-8 md:w-8",
                  isMaterialsClassroomActive(classroom.id)
                    ? "bg-inset text-ink"
                    : "text-ink-subtle hover:bg-inset hover:text-ink",
                )}
              >
                <FileText className="h-3.5 w-3.5" aria-hidden />
              </NavLink>
            </li>
          ))}
        </CollapsibleNavSection>
      </nav>

      {/* Pinned tray above the user menu. PRD-28 — "Feedback" sits
          above "Extraer texto" so a professor reporting a bug doesn't
          have to scroll past the tools section to find it. Lives
          outside the scrollable <nav> so it's always reachable. */}
      <div className="border-hairline space-y-1 border-t px-3 py-2">
        <FeedbackButton onCloseMobile={onCloseMobile} />
        <NavLink
          to="/herramientas/escanear"
          onClick={onCloseMobile}
          className={({ isActive }) => navItemClass(isActive)}
        >
          <ScanText className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1 truncate">{t("nav.tools.scan_grade")}</span>
        </NavLink>
      </div>

      <div ref={userMenuRef} className="border-hairline relative border-t">
        <button
          type="button"
          onClick={() => setUserMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          aria-label={t("nav.aria.user_menu")}
          className="hover:bg-inset flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150"
        >
          <Avatar
            name={professor.full_name || professor.name}
            initials={professor.initials}
            color={professor.color}
            size="md"
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-ink truncate text-sm font-semibold">
              {professor.full_name || professor.name}
            </p>
            <p className="text-meta truncate">{professor.school?.name ?? ""}</p>
          </div>
          <ChevronDown
            className={cn(
              "text-ink-subtle h-4 w-4 shrink-0 transition-transform duration-200",
              userMenuOpen ? "-rotate-180" : "rotate-0",
            )}
            aria-hidden="true"
          />
        </button>

        {userMenuOpen && (
          <div
            role="menu"
            className="rounded-card border-border bg-surface shadow-float absolute right-3 bottom-full left-3 z-10 mb-2 overflow-hidden border"
          >
            {/* The theme picker is a radiogroup, so it uses the radiogroup
                primitive: arrow keys, 44px options and the tokens, none of
                which the three hand-rolled 24px buttons here had. It stays in
                this menu rather than moving to Configuración because it is a
                per-device preference a professor flips at night, not a setting
                she goes looking for. */}
            <div className="border-hairline border-b px-3 py-2">
              <p className="text-label mb-1.5">{t("theme.label")}</p>
              <Segmented
                ariaLabel={t("nav.aria.toggle_theme")}
                value={theme}
                onChange={setTheme}
                options={THEME_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.labelKey),
                  icon: option.icon,
                }))}
              />
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={handleSettings}
              className="text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 w-full items-center gap-3 px-3 text-left text-sm"
            >
              <Settings className="text-ink-subtle h-4 w-4" aria-hidden />
              <span>{t("actions.settings")}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="border-hairline text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 w-full items-center gap-3 border-t px-3 text-left text-sm"
            >
              <LogOut className="text-ink-subtle h-4 w-4" aria-hidden />
              <span>{t("actions.logout")}</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

// ── Collapsible sidebar section ──────────────────────────────────────
// The shell behind "MIS SALONES" and "MIS MATERIALES": uppercase header
// button, fixed-position +/− tile (PRD-01 §FR-6 — not a rotating chevron),
// and the list container. Callers own the rows.
function CollapsibleNavSection({
  id,
  title,
  listLabel,
  open,
  onToggle,
  children,
}: {
  id: string
  title: string
  listLabel: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        // No `aria-label`: it used to repeat the job ("Mostrar u ocultar mis
        // salones") and, because an aria-label REPLACES the accessible name,
        // a screen reader never heard the section's own title. The visible
        // text plus `aria-expanded` already say both things.
        className="text-label hover:text-ink flex min-h-11 w-full items-center justify-between px-3 md:min-h-8"
      >
        <span>{title}</span>
        <span
          className="rounded-control border-border bg-canvas text-ink-muted flex h-5 w-5 items-center justify-center border"
          aria-hidden="true"
        >
          {open ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        </span>
      </button>

      {open && (
        <ul id={id} className="mt-1 space-y-1" aria-label={listLabel}>
          {children}
        </ul>
      )}
    </div>
  )
}

// ── PRD-28 — pinned "Feedback" entry ─────────────────────────────────
// Sits above the "Extraer texto" tools link. Visual treatment mirrors
// the existing NavLink (same padding + hover + dark-mode tokens) so
// the two tray entries read as siblings. A button (not a NavLink)
// because it opens the FeedbackModal instead of routing.
function FeedbackButton({ onCloseMobile }: { onCloseMobile?: () => void }) {
  const { t } = useTranslation("common")
  const { openFeedback } = useFeedback()
  return (
    <button
      type="button"
      onClick={() => {
        openFeedback()
        onCloseMobile?.()
      }}
      className="rounded-control text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 w-full items-center gap-3 px-3 text-sm transition-colors duration-150 md:min-h-9"
    >
      <MessageSquare className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate text-left">{t("nav.feedback")}</span>
    </button>
  )
}
